import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import PickCard from "@/components/PickCard";
import Disclaimer from "@/components/Disclaimer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import { BET_LABEL, SPORT_META, bettingDayBounds, todayBettingDatePT, nextBettingDatePT } from "@/lib/picks";
import { fetchPickAccessMap } from "@/lib/access";
import { Sparkles, ArrowUpRight, Filter, Lightbulb, ArrowDownUp } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PicksTabs from "@/components/PicksTabs";
import SegmentedToggle from "@/components/SegmentedToggle";
import { ROLLOVER_HINT } from "@/lib/slate";

type SortKey = "confidence" | "edge" | "units" | "newest";
type SlateView = "today" | "tomorrow" | "upcoming" | "history";

export default function AllPicks() {
  const { user, isPro, isAdmin } = useAuth();
  const [picks, setPicks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sport, setSport] = useState("all");
  const [bet, setBet] = useState("all");
  const [risk, setRisk] = useState("all");
  const [minConf, setMinConf] = useState(0);
  const [minUnits, setMinUnits] = useState(0);
  const [sort, setSort] = useState<SortKey>("confidence");
  const [day, setDay] = useState<{ start: string; end: string } | null>(null);
  const [view, setView] = useState<SlateView>("today");

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    (async () => {
      const bounds = bettingDayBounds();
      setDay(bounds);
      // Wide window: 30 days back through 30 days forward — covers all four views.
      const today = todayBettingDatePT();
      const lo = new Date(today + "T12:00:00Z"); lo.setUTCDate(lo.getUTCDate() - 30);
      const hi = new Date(today + "T12:00:00Z"); hi.setUTCDate(hi.getUTCDate() + 30);
      const loStr = lo.toISOString().slice(0, 10);
      const hiStr = hi.toISOString().slice(0, 10);
      const { data } = await supabase
        .from("picks")
        .select("*, sports(code)")
        .eq("status", "published")
        .is("deleted_at", null)
        .filter("betting_date", "gte", loStr)
        .filter("betting_date", "lte", hiStr)
        .order("game_start_time", { ascending: true });
      const ids = (data ?? []).map((p) => p.id);
      // Use today's bounds for entitlement check; access.ts handles per-pick logic
      const accessMap = await fetchPickAccessMap(user.id, bounds.start, bounds.end, false, false, ids);
      setPicks((data ?? []).map((p) => ({ ...p, access: accessMap.get(p.id) })));
      setLoading(false);
    })();
  }, [user?.id]);

  const today = todayBettingDatePT();
  const tomorrow = nextBettingDatePT();

  const counts = useMemo(() => {
    const c = { today: 0, tomorrow: 0, upcoming: 0, history: 0 };
    picks.forEach((p) => {
      const bd = p.betting_date as string | undefined;
      if (!bd) return;
      if (bd === today) c.today++;
      else if (bd === tomorrow) c.tomorrow++;
      else if (bd > tomorrow) c.upcoming++;
      else if (bd < today) c.history++;
    });
    return c;
  }, [picks, today, tomorrow]);

  const viewPicks = useMemo(() => picks.filter((p) => {
    const bd = p.betting_date as string | undefined;
    if (!bd) return false;
    if (view === "today") return bd === today;
    if (view === "tomorrow") return bd === tomorrow;
    if (view === "upcoming") return bd > tomorrow;
    return bd < today;
  }), [picks, view, today, tomorrow]);

  const filtered = useMemo(
    () => viewPicks.filter((p) =>
      (sport === "all" || p.sports?.code === sport) &&
      (bet === "all" || p.bet_type === bet) &&
      (risk === "all" || p.risk === risk) &&
      (p.confidence_score ?? 0) >= minConf &&
      (p.unit_rating ?? 0) >= minUnits,
    ),
    [viewPicks, sport, bet, risk, minConf, minUnits],
  );

  const sorted = useMemo(() => {
    const arr = [...filtered];
    switch (sort) {
      case "confidence": arr.sort((a, b) => (b.confidence_score ?? 0) - (a.confidence_score ?? 0)); break;
      case "edge": arr.sort((a, b) => (b.confidence_score ?? 0) - (a.confidence_score ?? 0)); break;
      case "units": arr.sort((a, b) => (b.unit_rating ?? 0) - (a.unit_rating ?? 0)); break;
      case "newest": arr.sort((a, b) => new Date(b.published_at ?? 0).getTime() - new Date(a.published_at ?? 0).getTime()); break;
    }
    return arr;
  }, [filtered, sort]);

  // Editorial insight: detect dominant sport/market with strongest model lean
  const editorial = useMemo(() => {
    if (picks.length < 3) return null;
    const groups: Record<string, { count: number; conf: number }> = {};
    picks.forEach((p) => {
      const key = `${p.sports?.code ?? "—"} ${BET_LABEL[p.bet_type] ?? p.bet_type}`;
      groups[key] = groups[key] ?? { count: 0, conf: 0 };
      groups[key].count++;
      groups[key].conf += Number(p.confidence_score ?? 0);
    });
    const top = Object.entries(groups)
      .map(([k, v]) => ({ k, count: v.count, avg: v.conf / v.count }))
      .sort((a, b) => (b.avg * b.count) - (a.avg * a.count))[0];
    if (!top || top.count < 2) return null;
    return `Model leaning toward ${top.k.toLowerCase()} today — ${top.count} plays averaging ${Math.round(top.avg)}/100 confidence.`;
  }, [picks]);

  // Featured pick: highest confidence unlocked play
  const featuredId = useMemo(() => {
    const unlocked = picks.filter((p) => !(p.access?.is_locked ?? true));
    if (!unlocked.length) return null;
    return unlocked.sort((a, b) => (b.confidence_score ?? 0) - (a.confidence_score ?? 0))[0].id;
  }, [picks]);

  const showUpgrade = !isPro && !isAdmin;

  return (
    <div className="container py-8 md:py-12">
      <Seo
        title="All Picks Today — AI Sports Betting Board | AleaEdge"
        description="Browse every AI-graded pick across MLB, NBA, NFL, NHL and Soccer with confidence scores, odds and live filters."
        path="/picks"
      />
      <div className="flex flex-col gap-4">
        <PicksTabs className="self-start" />
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl md:text-5xl font-bold leading-tight">
              {view === "today" && <>Today's <span className="te-grad-text">Edge</span></>}
              {view === "tomorrow" && <>Tomorrow's <span className="te-grad-text">Slate</span></>}
              {view === "upcoming" && <><span className="te-grad-text">Upcoming</span> Plays</>}
              {view === "history" && <>Pick <span className="te-grad-text">History</span></>}
            </h1>
            <p className="text-muted-foreground mt-2 text-sm">
              {day ? new Date(day.start).toLocaleDateString("en-US", { timeZone: "America/Los_Angeles", weekday: "long", month: "long", day: "numeric" }) + " · PT" : "—"}
              <span className="mx-2 opacity-50">·</span>
              <span className="tabular-nums">{viewPicks.length}</span> {view === "history" ? "settled / past" : "published"} plays
              <span className="block sm:inline sm:ml-2 mt-1 sm:mt-0 text-[11px] opacity-70">{ROLLOVER_HINT}</span>
            </p>
          </div>
        </div>
        <SegmentedToggle<SlateView>
          ariaLabel="Slate view"
          className="self-start"
          value={view}
          onChange={setView}
          options={[
            { value: "today", label: `Today${counts.today ? ` (${counts.today})` : ""}` },
            { value: "tomorrow", label: `Tomorrow${counts.tomorrow ? ` (${counts.tomorrow})` : ""}` },
            { value: "upcoming", label: `Upcoming${counts.upcoming ? ` (${counts.upcoming})` : ""}` },
            { value: "history", label: `History${counts.history ? ` (${counts.history})` : ""}` },
          ]}
        />
        {editorial && (
          <div className="hidden md:flex items-center gap-2 max-w-md text-xs text-muted-foreground bg-secondary/30 border border-border/50 rounded-2xl px-3 py-2">
            <Lightbulb className="h-3.5 w-3.5 text-accent shrink-0" />
            <span>{editorial}</span>
          </div>
        )}
        {editorial && (
          <div className="md:hidden flex items-center gap-2 text-xs text-muted-foreground bg-secondary/30 border border-border/50 rounded-2xl px-3 py-2">
            <Lightbulb className="h-3.5 w-3.5 text-accent shrink-0" />
            <span>{editorial}</span>
          </div>
        )}
      </div>

      {showUpgrade && (
        <div className="relative my-6 overflow-hidden rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/[0.08] via-secondary/40 to-transparent p-5 md:p-6">
          <div className="absolute -top-16 -right-10 h-48 w-48 rounded-full bg-gradient-accent opacity-20 blur-3xl pointer-events-none" />
          <div className="relative flex items-center justify-between gap-5 flex-wrap">
            <div className="max-w-xl">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-accent font-semibold">
                <Sparkles className="h-3.5 w-3.5" /> Pro Membership
              </div>
              <h2 className="font-display text-xl md:text-2xl font-bold mt-1.5 leading-tight">
                Unlock every premium pick, full AI breakdowns, and Parlay of the Day.
              </h2>
              <p className="text-xs md:text-sm text-muted-foreground mt-1.5">
                Verified record · transparent settlement · cancel anytime.
              </p>
            </div>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-accent px-5 py-3 text-sm font-bold text-accent-foreground shadow-glow hover:opacity-95 transition"
            >
              Go Pro — $19.99/mo <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}

      {/* Filter + sort bar */}
      <div className="te-card p-3 md:p-4 my-5">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
            <Filter className="h-3.5 w-3.5" /> Filters
          </div>
          <div className="flex items-center gap-2">
            <ArrowDownUp className="h-3.5 w-3.5 text-muted-foreground" />
            <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
              <SelectTrigger className="h-8 text-xs w-[170px] border-border/60 bg-background/40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="confidence">Highest Confidence</SelectItem>
                <SelectItem value="edge">Best Edge</SelectItem>
                <SelectItem value="units">Highest Units</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <FilterSelect label="Sport" value={sport} onChange={setSport} options={[["all", "All sports"], ...Object.entries(SPORT_META).map(([k, v]) => [k, `${v.icon} ${v.label}`] as [string, string])]} />
          <FilterSelect label="Market" value={bet} onChange={setBet} options={[["all", "All markets"], ...Object.entries(BET_LABEL) as [string, string][]]} />
          <FilterSelect label="Risk" value={risk} onChange={setRisk} options={[["all", "Any risk"], ["low", "Low"], ["medium", "Medium"], ["high", "High"]]} />
          <SliderField label="Min Confidence" value={minConf} suffix="" max={100} step={5} onChange={setMinConf} />
          <SliderField label="Min Units" value={minUnits} suffix="u" max={5} step={1} onChange={setMinUnits} />
        </div>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-[280px] rounded-2xl" />)}
        </div>
      ) : sorted.length === 0 ? (
        <div className="te-card p-12 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-secondary/60 grid place-items-center mb-3">
            <Filter className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="font-medium">No picks match these filters.</div>
          <div className="text-xs text-muted-foreground mt-1">Try widening risk, confidence, or unit thresholds.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {sorted.map((p) => (
            <PickCard
              key={p.id}
              pick={p}
              sportCode={p.sports?.code ?? "NBA"}
              locked={p.access?.is_locked ?? true}
              featured={p.id === featuredId}
            />
          ))}
        </div>
      )}

      <div className="mt-12"><Disclaimer compact /></div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <div>
      <label className="text-[10px] uppercase text-muted-foreground tracking-[0.15em]">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1.5 h-9 text-xs border-border/60 bg-background/40"><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function SliderField({ label, value, suffix, max, step, onChange }: { label: string; value: number; suffix: string; max: number; step: number; onChange: (v: number) => void }) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between">
        <label className="text-[10px] uppercase text-muted-foreground tracking-[0.15em]">{label}</label>
        <span className="text-[11px] tabular-nums text-foreground/80">{value}{suffix}</span>
      </div>
      <Slider value={[value]} onValueChange={(v) => onChange(v[0])} min={0} max={max} step={step} className="mt-3" />
    </div>
  );
}
